/**
 * The Workflows page's rules (`docs/proposals/workflows.md`, "Listing"), and how a
 * workflow's view picks the section to open.
 *
 * The cards come from the agent: `GET /workflows` lists each workflow with its
 * state, readiness, the **sections** its view is composed of, and text in both
 * languages (`lib/workflowList.ts`). This build keeps no table of its own: a
 * workflow the agent lists as open can be entered, and its view draws the
 * declared sections it knows (`WORKFLOW_SECTIONS` in `./index.ts`) and nothing
 * else — so a new workflow is a registration on the agent side, and web code
 * only for a custom section.
 *
 * Kept free of runtime imports so the rules can be tested on their own
 * (`tests/workflows.test.mjs` transpiles this file alone); the type imports erase.
 */

import type { WorkflowCategory, WorkflowListing, WorkflowSectionId } from "../../../shared/workflow-listing";

export type { WorkflowCategory, WorkflowListing, WorkflowSectionId };

/** In page order. A category with no card (Domain experts, for now) is not drawn. */
export const WORKFLOW_CATEGORIES: readonly WorkflowCategory[] = ["interoperability", "modeling", "domain"];

/** The standard sections, in the order a view shows them — the agent's order too. */
export const WORKFLOW_SECTION_ORDER: readonly WorkflowSectionId[] = ["setup", "progress", "results"];

export type OpenWorkflow = WorkflowListing;

/**
 * The workflow a view can be entered for, by its **code** (the route key, what
 * a conversation's snapshot names): listed by the agent as open. Null for a
 * Coming soon card or a code the agent does not list (PR #210's "general", say).
 */
export function openWorkflow(code: string | null | undefined, listed: readonly WorkflowListing[]): OpenWorkflow | null {
  if (!code) return null;
  const listing = listed.find((item) => item.code === code);
  return listing && listing.state === "open" ? listing : null;
}

/** Where a conversation stands: not started, a turn running, or saved and idle. */
export type WorkflowPhase = "new" | "running" | "saved";

/**
 * The section a workflow's view opens on when none is chosen: the start form for
 * a new conversation; the run's steps while a turn runs (null until the first
 * one is written — the view shows "starting"); for a saved conversation, the
 * first that has something to show — the report, the steps, the form.
 * `available` says whether a declared section has a tab to open right now.
 */
export function defaultSection(
  sections: readonly WorkflowSectionId[],
  phase: WorkflowPhase,
  available: (id: WorkflowSectionId) => boolean,
): WorkflowSectionId | null {
  const has = (id: WorkflowSectionId) => sections.includes(id);
  if (phase === "new") return has("setup") ? "setup" : null;
  if (phase === "running") return has("progress") && available("progress") ? "progress" : null;
  const order: readonly WorkflowSectionId[] = ["results", "progress", "setup"];
  return order.find((id) => has(id) && available(id)) ?? null;
}

export type WorkflowSort = "category" | "available" | "name";

/** The page's default order (owner, 2026-10-10): the cards grouped by category. */
export const DEFAULT_WORKFLOW_SORT: WorkflowSort = "category";

/**
 * The Workflows page's one list: filtered by category and by a search over the
 * card's title and description (as shown, in the reader's language), then sorted.
 * "category" groups the cards by category in page order (`WORKFLOW_CATEGORIES`),
 * open cards first within each group and catalogue order after that; "available"
 * puts the open cards first and keeps catalogue order within each group; "name"
 * is alphabetical by title.
 */
export function listWorkflows(
  items: readonly WorkflowListing[],
  options: { query: string; category: WorkflowCategory | "all"; sort: WorkflowSort },
  text: (item: WorkflowListing) => { title: string; description: string },
  isOpen: (item: WorkflowListing) => boolean,
): WorkflowListing[] {
  const query = options.query.trim().toLocaleLowerCase();
  const shown = items.filter((item) => {
    if (options.category !== "all" && item.category !== options.category) return false;
    if (!query) return true;
    const { title, description } = text(item);
    return `${title}\n${description}`.toLocaleLowerCase().includes(query);
  });
  if (options.sort === "name") {
    return [...shown].sort((a, b) => text(a).title.localeCompare(text(b).title));
  }
  const availableFirst = [...shown.filter(isOpen), ...shown.filter((item) => !isOpen(item))];
  if (options.sort === "available") return availableFirst;
  // A category the page does not know sorts last, so a newer agent's card still shows.
  const rank = (item: WorkflowListing): number => {
    const index = WORKFLOW_CATEGORIES.indexOf(item.category);
    return index === -1 ? WORKFLOW_CATEGORIES.length : index;
  };
  // Stable, so the "available first" order holds inside each category.
  return availableFirst.sort((a, b) => rank(a) - rank(b));
}
